// Donna's resolver (2026-07-23) — the decide-rail panel for
// hh-match-quarantine ("Where does the imported record belong?").
//
// Before this panel, the recommended action said "Open to resolve the
// match" but pointed at a review screen that no longer exists (torn
// down with the admin reconciliation page) — a hard dead end, and the
// candidate households all rendered with the SAME label so a steward
// couldn't tell five "Abbi Smith" candidates apart. This panel shows
// LIVE data: who the import row is, which family their email resolves
// to, and every candidate with member names + city + services — then
// resolves in ONE click per Grant's ruling ("a one or two click way to
// resolve the issue").
//
// The two actions map to the two decide verbs the server added:
//   quarantine-match  → merge the picked candidate into the primary's
//                       household (proven mergeHouseholds substrate —
//                       versioned, audited, reversible from History)
//   skip-import-row   → none of these fit; the row is skipped and no
//                       live record changes.

import { useEffect, useState } from "react";

import {
  api,
  type CleanupIssueSummary,
  type QuarantineResolveContext,
} from "../api";

const sectionLabel: React.CSSProperties = {
  fontFamily: "var(--heading-font-web)",
  fontWeight: 800,
  fontSize: 10.5,
  textTransform: "uppercase",
  letterSpacing: "0.1em",
  color: "var(--blue-dark)",
};

const candidateCard: React.CSSProperties = {
  border: "1px solid var(--mid-gray)",
  borderRadius: 10,
  padding: "10px 12px",
  marginTop: 8,
};

const actionBtn: React.CSSProperties = {
  fontSize: 12.5,
  fontWeight: 700,
  padding: "6px 12px",
  borderRadius: 6,
  border: "1px solid var(--blue-dark)",
  background: "var(--blue)",
  color: "var(--white)",
  cursor: "pointer",
};

const quietBtn: React.CSSProperties = {
  fontSize: 12.5,
  fontWeight: 600,
  padding: "6px 12px",
  borderRadius: 6,
  border: "1px solid var(--mid-gray)",
  background: "var(--bg-0)",
  color: "var(--navy)",
  cursor: "pointer",
};

export function QuarantineMatchPanel({
  issue,
  onResolved,
}: {
  issue: CleanupIssueSummary;
  onResolved: () => void;
}) {
  const [ctx, setCtx] = useState<QuarantineResolveContext | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null); // household id or "skip"
  const [actionError, setActionError] = useState<string | null>(null);
  const [doneMsg, setDoneMsg] = useState<string | null>(null);
  // The survivor the steward designated, when the row's email can no
  // longer locate the family (Grant 2026-07-29). Null until they choose —
  // the system never pre-picks a merge destination it isn't sure of.
  const [pickedDestId, setPickedDestId] = useState<string | null>(null);
  // hh-match-quarantine is confirm-tier, so POST /decide requires a
  // reason code on the merge — and this panel replaces the generic
  // decide rail, so until now there was nowhere to give one and every
  // merge 400'd with "pick one and resend" (Donna, 2026-07-29).
  // Skipping is exempt server-side (it changes no live record).
  const [reasonCodes, setReasonCodes] = useState<
    Array<{ code: string; display_label: string; description: string }>
  >([]);
  const [reasonCode, setReasonCode] = useState<string>("");

  useEffect(() => {
    let alive = true;
    setCtx(null);
    setLoadError(null);
    setDoneMsg(null);
    setActionError(null);
    setPickedDestId(null);
    setReasonCode("");
    // Merge-applicable codes only — the same filtered catalog the decide
    // rail uses, so an archive-only code can never appear on a merge.
    api
      .listChangeReasonCodes("merge")
      .then((r) => {
        if (alive) setReasonCodes(r.codes);
      })
      .catch(() => {
        if (alive) setReasonCodes([]);
      });
    api
      .getQuarantineContext(issue.id)
      .then((c) => {
        if (alive) setCtx(c);
      })
      .catch(() => {
        if (alive)
          setLoadError(
            "Couldn't load this import row's details — try again, or flag it for an admin.",
          );
      });
    return () => {
      alive = false;
    };
  }, [issue.id]);

  async function act(verb: "quarantine-match" | "skip-import-row", targetId?: string) {
    if (!ctx || busy) return;
    setBusy(targetId ?? "skip");
    setActionError(null);
    try {
      await api.decideCleanupIssue({
        issue_id: issue.id,
        verb,
        params: {
          quarantine_id: ctx.quarantine_id,
          ...(targetId ? { target_household_id: targetId } : {}),
          // Only when the row's email couldn't find the family and the
          // steward designated the survivor themselves.
          ...(!ctx.primary_household && pickedDestId
            ? { primary_household_id: pickedDestId }
            : {}),
        },
        // Required on the merge (confirm tier); skip is exempt.
        ...(verb === "quarantine-match" && reasonCode
          ? { reason_code: reasonCode }
          : {}),
      });
      setDoneMsg(
        verb === "quarantine-match"
          ? "Merged — this family is now one household. Reversible from the household's History."
          : "Import row skipped — nothing changed on any live record.",
      );
      onResolved();
    } catch (err) {
      setActionError(
        err instanceof Error && err.message
          ? err.message
          : "That didn't go through — try again.",
      );
    } finally {
      setBusy(null);
    }
  }

  if (doneMsg) {
    return (
      <div style={{ fontSize: 13, color: "var(--navy)" }}>{doneMsg}</div>
    );
  }
  if (loadError) {
    return (
      <div style={{ fontSize: 13, color: "var(--danger)" }}>{loadError}</div>
    );
  }
  if (!ctx) {
    return (
      <div style={{ fontSize: 13, color: "var(--fg-muted)" }}>
        Loading the import row…
      </div>
    );
  }

  const primary = ctx.primary_household;
  const realCandidates = ctx.candidates.filter((c) => !c.is_primary_household);

  return (
    <div>
      {/* What this even is — plain words first (UX rule #1). */}
      <div style={{ fontSize: 13, color: "var(--dark-gray)", lineHeight: 1.45 }}>
        This is a leftover row from the ActiveCampaign import: the
        spreadsheet said this person has a spouse, and the matcher found
        more than one household that could be that family — so it parked
        the row for a human instead of guessing.
      </div>

      <div style={{ ...sectionLabel, marginTop: 12 }}>What the import said</div>
      <div style={{ fontSize: 13, color: "var(--navy)", marginTop: 4 }}>
        <b>{ctx.imported.name ?? "(no name)"}</b>
        {ctx.imported.spouse_name_raw && (
          <> · spouse listed as <b>{ctx.imported.spouse_name_raw}</b></>
        )}
        {ctx.imported.email && (
          <div style={{ color: "var(--fg-muted)", fontSize: 12.5 }}>
            {ctx.imported.email}
          </div>
        )}
      </div>

      <div style={{ ...sectionLabel, marginTop: 12 }}>
        The family it belongs to
      </div>
      {primary ? (
        <div style={{ fontSize: 13, color: "var(--navy)", marginTop: 4 }}>
          <b>{primary.display_name}</b>
          <div style={{ color: "var(--fg-muted)", fontSize: 12.5 }}>
            Members: {primary.members.join(", ") || "(none)"}
          </div>
        </div>
      ) : (
        <div style={{ fontSize: 12.5, color: "var(--fg-muted)", marginTop: 4 }}>
          The email on this import row (
          {ctx.imported.email ?? "none"}) doesn't belong to anyone in the
          system anymore, so we can't tell you which household is the
          family — but one of the candidates below usually is. Pick the
          one to keep and we'll merge the other into it.
        </div>
      )}

      {/* No email-derived family → the steward designates the survivor.
          Deliberately NOT guessed by name: a wrong household merge is the
          mistake reversibility-first exists to prevent, and the evidence
          on each card below is what the choice should rest on. */}
      {!primary && realCandidates.length > 0 && (
        <>
          <div style={{ ...sectionLabel, marginTop: 12 }}>
            Which household is the family? (this one is kept)
          </div>
          <div
            style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}
          >
            {realCandidates.map((cand) => {
              const on = pickedDestId === cand.household_id;
              return (
                <button
                  key={cand.household_id}
                  type="button"
                  disabled={busy !== null}
                  onClick={() =>
                    setPickedDestId(on ? null : cand.household_id)
                  }
                  style={{
                    ...quietBtn,
                    borderColor: on ? "var(--blue)" : "var(--mid-gray)",
                    background: on ? "var(--blue-tint)" : "var(--bg-0)",
                    fontWeight: on ? 800 : 600,
                  }}
                >
                  {on ? "✓ " : ""}
                  Keep {cand.display_name}
                </button>
              );
            })}
          </div>
          {!pickedDestId && (
            <div
              style={{
                fontSize: 12.5,
                color: "var(--fg-muted)",
                marginTop: 6,
              }}
            >
              Pick one to keep, then merge the other into it. If none of
              them is this family, skip the row instead.
            </div>
          )}
        </>
      )}

      <div style={{ ...sectionLabel, marginTop: 12 }}>
        Is one of these the same family?
      </div>

      {/* Merging households is confirm-tier, so the server requires a
          reason code. It lives here, ABOVE the merge buttons, because a
          rule you only learn about by failing is a dead end (UX rule #5).
          Skipping needs none — it changes no live record. */}
      {realCandidates.length > 0 && reasonCodes.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <label
            htmlFor="quarantine-reason-code"
            style={{ ...sectionLabel, display: "block" }}
          >
            Why are these one family? (required to merge)
          </label>
          <select
            id="quarantine-reason-code"
            value={reasonCode}
            disabled={busy !== null}
            onChange={(e) => setReasonCode(e.target.value)}
            style={{
              marginTop: 4,
              width: "100%",
              padding: "6px 8px",
              borderRadius: 6,
              border: "1px solid var(--mid-gray)",
              background: "var(--bg-0)",
              color: "var(--navy)",
              fontSize: 12.5,
            }}
          >
            <option value="">Pick a reason…</option>
            {reasonCodes.map((rc) => (
              <option key={rc.code} value={rc.code}>
                {rc.display_label}
              </option>
            ))}
          </select>
          {reasonCode !== "" && (
            <div
              style={{
                fontSize: 12,
                color: "var(--fg-muted)",
                marginTop: 4,
                lineHeight: 1.4,
              }}
            >
              {reasonCodes.find((rc) => rc.code === reasonCode)?.description}
            </div>
          )}
        </div>
      )}
      {realCandidates.length === 0 && (
        <div style={{ fontSize: 12.5, color: "var(--fg-muted)", marginTop: 4 }}>
          No other candidate households — if everything above already
          looks right, skip the row.
        </div>
      )}
      {realCandidates.map((cand) => {
        // Destination = the email-derived family, else the one the
        // steward picked. Without either, merging has no target and the
        // button stays disabled WITH a reason (UX rule #5).
        const isDestination =
          !primary && pickedDestId === cand.household_id;
        const destination =
          primary?.display_name ??
          (pickedDestId
            ? (realCandidates.find((c) => c.household_id === pickedDestId)
                ?.display_name ?? null)
            : null);
        // Both preconditions the server enforces, checked here so the
        // button says what's missing instead of failing after the click.
        const canMerge =
          Boolean(destination) && !isDestination && reasonCode !== "";
        return (
        <div key={cand.household_id} style={candidateCard}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "var(--navy)" }}>
            {cand.display_name}
          </div>
          <div style={{ fontSize: 12.5, color: "var(--fg-muted)", marginTop: 2 }}>
            {cand.members.length > 0
              ? `Members: ${cand.members.join(", ")}`
              : "No live members"}
            {(cand.city || cand.zip) && (
              <> · {[cand.city, cand.state, cand.zip].filter(Boolean).join(", ")}</>
            )}
            {" · "}
            {cand.active_services} active service
            {cand.active_services === 1 ? "" : "s"}
          </div>
          <div style={{ marginTop: 8 }}>
            {isDestination ? (
              // The survivor the steward picked — nothing to merge INTO
              // itself; say so plainly rather than showing a dead button.
              <div style={{ fontSize: 12.5, color: "var(--blue-dark)", fontWeight: 700 }}>
                Keeping this household — merge the other into it below.
              </div>
            ) : (
              <button
                style={{
                  ...actionBtn,
                  ...(canMerge ? {} : { opacity: 0.5, cursor: "not-allowed" }),
                }}
                disabled={!canMerge || busy !== null}
                onClick={() => void act("quarantine-match", cand.household_id)}
                title={
                  !destination
                    ? "Pick which household to keep first — then this merges the other into it."
                    : reasonCode === ""
                      ? "Pick a reason above first — merging households needs one on the record."
                      : `Merges "${cand.display_name}" into "${destination}" — one household, everything kept, reversible from History.`
                }
              >
                {busy === cand.household_id
                  ? "Merging…"
                  : destination
                    ? `Same family — merge into ${destination}`
                    : "Same family — merge into one household"}
              </button>
            )}
          </div>
        </div>
        );
      })}

      <div style={{ marginTop: 12 }}>
        <button
          style={quietBtn}
          disabled={busy !== null}
          onClick={() => void act("skip-import-row")}
          title="Records that none of the candidates fit. The import row is set aside; nothing changes on any live record."
        >
          {busy === "skip" ? "Skipping…" : "None of these — skip this import row"}
        </button>
      </div>
      {actionError && (
        <div style={{ fontSize: 12.5, color: "var(--danger)", marginTop: 8 }}>
          {actionError}
        </div>
      )}
    </div>
  );
}
